/**
 * B15 — ONE coordinate reader for `athlete_context._latitude` / `_longitude`.
 *
 * The founder's Today showed no weather row because his stored `_latitude` is
 * the jsonb STRING `"37.40"`: some writer stringified coordinates, the server
 * side already tolerates it (`parseFloat` at the weather endpoint), and every
 * client reader that required `typeof raw === 'number'` failed silent by
 * design. This helper mirrors the SERVER's tolerance — parseFloat +
 * Number.isFinite — so the client and the server read the same stored value
 * the same way.
 *
 * Contract:
 *   - finite number        → the number, unchanged
 *   - numeric string       → the parsed number ("37.40" → 37.4)
 *   - anything else (null, undefined, '', 'Sunnyvale', NaN, ±Infinity,
 *     objects) → null — a cleared or garbage location must never coerce to a
 *     coordinate (Number(null) is 0, which is a point in the Gulf of Guinea,
 *     not "no location"; that is exactly why this is parseFloat, not Number).
 *
 * The WRITE side is fenced separately (the server save chokepoint coerces and
 * §8.2-rounds numeric strings; supabase-migration-b15-coordinate-normalize.sql
 * repairs the stored rows) — this reader stays because a client must render
 * honestly against whatever row shape it is handed.
 */
export function contextCoord(raw: unknown): number | null {
  const num =
    typeof raw === 'number' ? raw
      : typeof raw === 'string' && raw.trim() !== '' ? Number.parseFloat(raw)
        : NaN;
  return Number.isFinite(num) ? num : null;
}
